import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";

import {
  getOptionLabel,
  getOptionValue,
  utilityTunnelConsumerType,
  utilityTunnelDepartments,
} from "common/enums";
import { onlyNumberKeys } from "utils";
import { useSelector } from "react-redux";
import { useLazyGetIssuanceRoleByDepartmentId } from "apis/UtilityTunnel/AccessCardDetails";

const ApplicantDetails = ({ formik, t, isEditable }) => {
  const [getIssuanceRole, { data: { data: issuanceRoles } = {} }] =
    useLazyGetIssuanceRoleByDepartmentId();

  const {
    values: {
      employeeContractorDetail: { departmentId },
      accessDetails: { accessCardHolderType },
      extraValues: { isRegisteringPersonSame } = {},
    },
    setFieldValue,
  } = formik;

  const { user } = useSelector((state) => state.Login);

  const isDisabled = useMemo(
    () => !user?.signatureImage || !user?.profileImage,
    [user?.signatureImage, user?.profileImage]
  );

  const isContractor = useMemo(
    () =>
      getOptionValue("utilityTunnelConsumerType", "Contractor") ===
      accessCardHolderType,
    [accessCardHolderType]
  );

  const isCapexRequired = useMemo(
    () =>
      utilityTunnelDepartments.find(({ value }) => value === departmentId)
        ?.isCapexOpex,
    [departmentId]
  );

  const updatedIssuanceRoleOption = useMemo(
    () =>
      issuanceRoles?.map(({ utaCardIssuanceRole }) => ({
        label: getOptionLabel("capexOpexOptionsType", utaCardIssuanceRole?.id),
        value: utaCardIssuanceRole?.id,
      })) ?? [],
    [issuanceRoles]
  );

  useEffect(() => {
    if (departmentId) {
      getIssuanceRole(departmentId);
    }
  }, [departmentId]);

  return (
    <>
      <Row my={5}>
        <Col className="d-flex align-items-center">
          <FormControl disabled={isEditable || isDisabled}>
            <RadioGroup name="authorizedPerson">
              <Grid container>
                <FormControlLabel
                  control={<Radio />}
                  label={t("Same as registered person")}
                  checked={isRegisteringPersonSame}
                  onChange={() =>
                    setFieldValue("extraValues.isRegisteringPersonSame", true)
                  }
                />
                <FormControlLabel
                  control={<Radio />}
                  checked={!isRegisteringPersonSame}
                  label={t("Different from registered person")}
                  onChange={() =>
                    setFieldValue("extraValues.isRegisteringPersonSame", false)
                  }
                />
              </Grid>
            </RadioGroup>
          </FormControl>
        </Col>
      </Row>
      <Row>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Access Card Holder Type") + "*"}
            name="accessDetails.accessCardHolderType"
            formik={formik}
            options={utilityTunnelConsumerType}
            disabled={isRegisteringPersonSame || isEditable || isDisabled}
          />
        </Col>
        {getOptionValue("utilityTunnelConsumerType", "Employee") ===
          accessCardHolderType && (
          <>
            <Col md={4} sm={6}>
              <InputFieldText
                label={t("Employee ID") + "*"}
                name="employeeContractorDetail.employeeId"
                formik={formik}
                placeholder="Enter Employee ID"
                disabled={isDisabled}
              />
            </Col>
            <Col md={4} sm={6}>
              <InputFieldText
                label={t("Designation")}
                name="employeeContractorDetail.designation"
                formik={formik}
                placeholder="Enter Desgination"
                disabled={isDisabled}
              />
            </Col>
          </>
        )}
        {isContractor && (
          <>
            <Col md={4} sm={6}>
              <InputFieldText
                label={t("Contractor Company Name") + "*"}
                name="employeeContractorDetail.companyName"
                formik={formik}
                placeholder="Enter Contractor company name"
                disabled={isDisabled}
              />
            </Col>
            <Col md={4} sm={6}>
              <InputFieldText
                label={t("Sub Contractor/Firm Name")}
                name="employeeContractorDetail.subContractor"
                formik={formik}
                placeholder="Enter sub contractor/firm name"
                disabled={isDisabled}
              />
            </Col>
          </>
        )}
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("First Name") + "*"}
            name="employeeContractorDetail.firstName"
            formik={formik}
            placeholder={"Enter First Name"}
            disabled={isRegisteringPersonSame || isDisabled}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Last Name") + "*"}
            name="employeeContractorDetail.lastName"
            formik={formik}
            placeholder={"Enter Last Name"}
            disabled={(isRegisteringPersonSame && user?.lastName) || isDisabled}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Email") + "*"}
            name="employeeContractorDetail.email"
            formik={formik}
            placeholder={"Enter Email"}
            disabled={isRegisteringPersonSame || isDisabled}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Mobile Number") + "*"}
            name="employeeContractorDetail.mobileNumber"
            formik={formik}
            type="number"
            InputProps={{
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            placeholder={t("Enter Mobile Number")}
            disabled={
              (isRegisteringPersonSame && user?.mobileNumber) || isDisabled
            }
          />
        </Col>
        <Col md={4} sm={6}>
          <SelectField
            label={
              isContractor
                ? `${t("Applied in GIFT CITY Department")}*`
                : `${t("GIFT CITY Department")}*`
            }
            name={"employeeContractorDetail.departmentId"}
            formik={formik}
            options={utilityTunnelDepartments}
            placeholder="Select Department"
            onChange={(_, option) => {
              setFieldValue(
                "employeeContractorDetail.departmentId",
                option.value
              );
              setFieldValue("employeeContractorDetail.roleId", "");
            }}
            disabled={isDisabled}
          />
        </Col>
        {isContractor && isCapexRequired && (
          <Col md={4} sm={6}>
            <SelectField
              label={t("Applied in GIFT CITY Section") + "*"}
              name="employeeContractorDetail.roleId"
              formik={formik}
              options={updatedIssuanceRoleOption || []}
              disabled={isDisabled}
            />
          </Col>
        )}
      </Row>
      <Row my={5}>
        {isContractor && (
          <Col>
            <InputFieldText
              label={t("Purpose Of Visit")}
              name="employeeContractorDetail.purposeOfVisit"
              formik={formik}
              placeholder="Enter Purpose Of Visit"
              disabled={isDisabled}
              multiline
              rows={2}
            />
          </Col>
        )}
      </Row>
    </>
  );
};

ApplicantDetails.propTypes = {
  formik: PropTypes.object,
  isEditable: PropTypes.bool,
  t: PropTypes.func,
};
export default withTranslation()(ApplicantDetails);
